import {
    LicenseProfile,
    RestrictionFilterOption,
    RoleBadgeVariant,
    RoleDefinition,
    UserAccount,
    UserGroup,
} from './user-management.types';

export function userSalutation(user: Pick<UserAccount, 'userId' | 'firstName' | 'lastName'> | null | undefined): string {
    if (!user?.userId) {
        return '';
    }

    const parts = [user.firstName, user.lastName].filter((part) => part != null && part !== '');
    if (parts.length === 0) {
        return user.userId;
    }

    return `${parts.join(' ')} (${user.userId})`;
}

export function sortUsers<T extends { owner?: boolean; lastName?: string }>(users: T[]): T[] {
    return [...users].sort((left, right) => {
        const ownerOrder = Number(Boolean(right.owner)) - Number(Boolean(left.owner));
        if (ownerOrder !== 0) {
            return ownerOrder;
        }

        const leftName = left.lastName ?? '';
        const rightName = right.lastName ?? '';
        if (leftName < rightName) {
            return -1;
        }
        if (leftName > rightName) {
            return 1;
        }
        return 0;
    });
}

export function uniqueNames(values: string[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    values.forEach((value) => {
        if (!seen.has(value)) {
            seen.add(value);
            result.push(value);
        }
    });
    return result;
}

export function isSystemRolesVisible(value: unknown): boolean {
    return value === true || value === 'true';
}

export function systemRoleNames(
    roleMetadataByName: Record<string, RoleDefinition>,
    organization: Record<string, unknown> | undefined,
    showSystemRoles: boolean,
): string[] {
    if (!showSystemRoles || !organization) {
        return [];
    }

    const organizationRole = organization.organizationRole;

    return Object.values(roleMetadataByName).flatMap((role) => {
        if (role.system !== true || !role.name) {
            return [];
        }
        if (role.name === '__system__shared_items') {
            return organization.itemNamespaceReadonly ? [role.name] : [];
        }
        if (role.name === '__system__buyer') {
            return organizationRole === 'INVITED_BUYER' ? [role.name] : [];
        }
        if (role.name === '__system__supplier') {
            return organizationRole === 'INVITED_SUPPLIER' ? [role.name] : [];
        }
        return [];
    });
}

export function displayRoles(accountRoles: string[] | undefined, systemRoles: string[]): string[] {
    return uniqueNames([...(accountRoles ?? []), ...systemRoles]);
}

export function effectiveRoleNames(
    groups: UserGroup[],
    accountRoles: string[] | undefined,
    systemRoles: string[],
): string[] {
    const fromGroups = groups.flatMap((group) => group.roles ?? []);
    return uniqueNames([...fromGroups, ...(accountRoles ?? []), ...systemRoles]);
}

export function isAssignableRole(role: string): boolean {
    return !role.startsWith('__system__');
}

export function roleBadgePresentation(role: RoleDefinition | undefined): { variant: RoleBadgeVariant; italic: boolean } {
    const italic = role?.system === true;
    if (role?.match === 'deny') {
        return { variant: 'error', italic };
    }
    if (role?.match === 'any') {
        return { variant: 'warning', italic };
    }
    if (italic) {
        return { variant: 'neutral', italic };
    }
    return { variant: 'info', italic: false };
}

export function filterLabel(filters: RestrictionFilterOption[], name: string | undefined): string | undefined {
    if (!name) {
        return undefined;
    }
    return filters.find((filter) => filter.name === name)?.label;
}

export function licenseUserCount(users: UserAccount[], pendingCount: number): number {
    const active = users.filter((user) => user.active && !user.systemAccount && !user.managingAccount);
    return active.length + pendingCount;
}

export function canManageUser(user: UserAccount): boolean {
    return !user.owner && !user.systemAccount && !user.managingAccount;
}

export function hasBothRestrictions(restrictionFilter?: string, restrictionQuery?: string): boolean {
    return Boolean(restrictionFilter) && Boolean(restrictionQuery);
}

export function isInviteDisabled(email?: string, roles?: string[]): boolean {
    return !email || !roles || roles.length === 0;
}

export function monthlyPriceMap(
    profile: LicenseProfile | undefined,
    planMonthlyPrice: Record<string, number> | undefined,
): Record<string, number> {
    if (!profile) {
        return {};
    }
    if (profile.monthlyPrice && Object.keys(profile.monthlyPrice).length > 0) {
        return profile.monthlyPrice;
    }
    if (profile.monthlyPrice == null && profile.monthlyPriceMultiplier != null && planMonthlyPrice) {
        const multiplier = profile.monthlyPriceMultiplier;
        return Object.fromEntries(
            Object.entries(planMonthlyPrice).map(([currency, amount]) => [currency, multiplier * amount]),
        );
    }
    return {};
}

export function yearlyPriceMap(monthly: Record<string, number>): Record<string, number> {
    return Object.fromEntries(
        Object.entries(monthly).map(([currency, amount]) => [currency, amount * 12]),
    );
}

export function formatCurrencyAmount(
    prices: Record<string, number> | undefined,
    currencyKey: string,
    symbol: string,
): string {
    if (!prices || Object.keys(prices).length === 0) {
        return '';
    }
    const amount = prices[currencyKey];
    return amount == null ? symbol : `${symbol}${amount}`;
}

export function localizedText(values: Record<string, string> | undefined, language: string, fallback = ''): string {
    if (!values) {
        return fallback;
    }
    return values[language] || fallback;
}
